import { useState } from "react";
import { Pencil, Trash2, Share2, CheckCircle2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useGet } from "../lib/hooks";
import { PageHeader, Pill } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { LIB_TYPES, LibraryWidgetView } from "../components/dashboard/LibraryWidgets";
import { METRICS } from "../components/dashboard/Widgets";

import { WidgetRequests } from "../components/features/WidgetRequests";

const CATEGORIES = ["Financial", "People", "Compliance", "Operations", "Wellbeing", "Marketing"];
const sel = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";

function Toggles({ options, value, onChange, testid }) {
  const t = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return <div className="flex flex-wrap gap-2">{options.map(([k, l]) => (
    <button key={k} type="button" onClick={() => t(k)} data-testid={`${testid}-${k}`}
      className={`rounded-sm border px-2.5 py-1 text-xs ${value.includes(k) ? "border-[#C2A462] bg-[#C2A462]/15 text-[#EBD6A0]" : "text-muted-foreground"}`}>{l}</button>))}</div>;
}

function EditDialog({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ category: "Financial", type: "financial_overview", config: {}, subtitle: "", ...initial });
  const up = (k, v) => setF({ ...f, [k]: v });
  const cfg = (k, v) => setF({ ...f, config: { ...f.config, [k]: v } });
  const save = async () => {
    try { f.id ? await api.put(`/widget-defs/${f.id}`, f) : await api.post("/widget-defs", f); toast.success("Widget saved"); onSaved(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="widget-def-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Edit widget" : "New widget"}</DialogTitle><DialogDescription>Widgets stay as drafts until you finalise them.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={f.name || ""} onChange={(e) => up("name", e.target.value)} data-testid="widget-def-name" /></div>
          <div><Label>Subtitle</Label><Input value={f.subtitle} onChange={(e) => up("subtitle", e.target.value)} data-testid="widget-def-subtitle" /></div>
          <div><Label>Category (gallery heading)</Label><Input list="widget-cats" value={f.category} onChange={(e) => up("category", e.target.value)} data-testid="widget-def-category" />
            <datalist id="widget-cats">{CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist></div>
          <div><Label>Widget type</Label><select className={sel} value={f.type} onChange={(e) => up("type", e.target.value)} data-testid="widget-def-type">
            {Object.entries(LIB_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>
            <p className="text-[11px] text-muted-foreground mt-1">Data source: {LIB_TYPES[f.type].source}</p></div>
          {f.type === "revenue_growth" && <div><Label>First financial year (e.g. 2024 for FY 2024–25)</Label><Input type="number" value={f.config.established_fy || ""} onChange={(e) => cfg("established_fy", e.target.value)} data-testid="widget-def-established" /></div>}
          {f.type === "stat_cards" && <Toggles testid="widget-def-metric" options={Object.entries(METRICS).map(([k, v]) => [k, v[0]])} value={f.config.metrics || []} onChange={(v) => cfg("metrics", v)} />}
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={!f.name} data-testid="widget-def-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShareDialog({ def, onClose, onSaved }) {
  const [accounts] = useGet("/accounts");
  const [plans] = useGet("/plans");
  const [s, setS] = useState(def.share || { account_ids: [], plan_ids: [] });
  const save = async () => {
    try { await api.put(`/widget-defs/${def.id}/share`, s); toast.success("Sharing updated"); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  const accs = (accounts?.accounts || accounts || []).filter((a) => !a.is_lab && a.status !== "cancelled");
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="widget-share-dialog">
        <DialogHeader><DialogTitle>Share "{def.name}"</DialogTitle><DialogDescription>Owners of these organisations can add it to their own or their team's dashboards.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><Label>Plans</Label><div className="mt-2"><Toggles testid="share-plan" options={(plans || []).map((p) => [p.id, p.name])} value={s.plan_ids} onChange={(v) => setS({ ...s, plan_ids: v })} /></div></div>
          <div><Label>Specific organisations</Label><div className="mt-2"><Toggles testid="share-account" options={accs.map((a) => [a.id, a.name])} value={s.account_ids} onChange={(v) => setS({ ...s, account_ids: v })} /></div></div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="widget-share-save">Save sharing</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DefCard({ w, onEdit, onShare, reload }) {
  const status = async (st) => { try { await api.put(`/widget-defs/${w.id}/status`, { status: st }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async () => { if (!window.confirm(`Delete "${w.name}"? Dashboards using it will show it as unavailable.`)) return; await api.delete(`/widget-defs/${w.id}`); reload(); };
  const n = (w.share?.account_ids?.length || 0) + (w.share?.plan_ids?.length || 0);
  return (
    <div className="space-y-3" data-testid={`widget-def-${w.id}`}>
      <LibraryWidgetView def={w} d={null} />
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={w.status === "final" ? "green" : "amber"}>{w.status === "final" ? "Final" : "Draft"}</Pill>
        <span className="text-xs text-slate-500">{n ? `Shared with ${n} plan/organisation${n > 1 ? "s" : ""}` : "Not shared"}</span>
        <div className="ml-auto flex gap-1">
          <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`widget-def-edit-${w.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
          {w.status === "draft"
            ? <Button size="sm" variant="outline" onClick={() => status("final")} data-testid={`widget-def-finalise-${w.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Finalise</Button>
            : <><Button size="sm" variant="outline" onClick={onShare} data-testid={`widget-def-share-${w.id}`}><Share2 className="h-3.5 w-3.5 mr-1" />Share</Button>
              <Button size="sm" variant="ghost" onClick={() => status("draft")} data-testid={`widget-def-draft-${w.id}`}><Undo2 className="h-3.5 w-3.5" /></Button></>}
          <Button size="sm" variant="ghost" onClick={del} data-testid={`widget-def-delete-${w.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
        </div>
      </div>
    </div>
  );
}

export default function WidgetGenerator({ embedded = false }) {
  const [list, reload] = useGet("/widget-defs");
  const [edit, setEdit] = useState(null);
  const [share, setShare] = useState(null);
  const groups = (list || []).reduce((g, w) => ({ ...g, [w.category || "General"]: [...(g[w.category || "General"] || []), w] }), {});
  return (
    <div data-testid="widget-generator-page">
      {embedded ? <WidgetRequests /> : <PageHeader overline="Features Lab" title="Widget Creator" desc="Request new widgets, then finalise and share the ones that are built." />}
      {!embedded && <WidgetRequests />}
      <h2 className="mt-10 mb-3 text-base md:text-lg font-semibold text-[#EBD6A0]">Built widgets</h2>
      <div className="rounded-xl bg-black p-6 space-y-10 min-h-[50vh]" data-testid="widget-gallery">
        {list && !list.length && <p className="text-sm text-slate-500">No widgets built yet. Save a request above and it will appear here once built.</p>}
        {Object.entries(groups).map(([cat, ws]) => (
          <section key={cat} data-testid={`widget-group-${cat}`}>
            <h2 className="text-base md:text-lg font-semibold text-[#EBD6A0] mb-4 border-b border-white/10 pb-2">{cat}</h2>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">{ws.map((w) => <DefCard key={w.id} w={w} reload={reload} onEdit={() => setEdit(w)} onShare={() => setShare(w)} />)}</div>
          </section>
        ))}
      </div>
      {edit && <EditDialog initial={edit} onClose={() => setEdit(null)} onSaved={reload} />}
      {share && <ShareDialog def={share} onClose={() => setShare(null)} onSaved={reload} />}
    </div>
  );
}
